import Phaser from "phaser";
import type { ZoneGrid } from "@/net/types";
import { MAP_SIZE, TILE, worldToScreen, type ScreenPos } from "./Iso";
import { InteractableTree } from "../objects/InteractableTree";
import { ensureWateringCanTexture } from "../objects/WateringCan";
import { ensurePropTextures, placeZoneDecorations } from "./ZoneProps";
import { CollisionMap } from "./CollisionMap";

export const TILE_TEXTURE_KEY = "iso-tile";
export const TILE_SIDE_S_KEY = "iso-tile-side-s";
export const TILE_SIDE_E_KEY = "iso-tile-side-e";
export const WATER_TEXTURE_KEY = "iso-water";
export const PROP_TEXTURE_KEY = "iso-prop";

/** Height (in pixels) per elevation unit. */
const ELEV_PX = 6;

/** Maximum elevation units. Must match server's Terrain @elev_max. */
const ELEV_MAX = 4;

export interface IsoMap {
  container: Phaser.GameObjects.Container;
  origin: ScreenPos;
  heightMap: number[][];
  waterMap: boolean[][];
  collision: CollisionMap;
  trees: InteractableTree[];
  decorations: Phaser.GameObjects.Image[];
}

/**
 * Generates the floor-tile and prop textures used by the procedural map.
 */
export function ensureMapTextures(scene: Phaser.Scene): void {
  if (!scene.textures.exists(TILE_TEXTURE_KEY)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    drawIsoDiamond(g, 0x2f6b3a, 0x1f4a28);
    g.generateTexture(TILE_TEXTURE_KEY, TILE.width, TILE.height);
    g.destroy();
  }
  if (!scene.textures.exists(TILE_SIDE_S_KEY)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    drawSideFaceS(g, 0x1f4a28, 0x143a1f);
    g.generateTexture(TILE_SIDE_S_KEY, TILE.halfW, TILE.halfH + ELEV_PX * ELEV_MAX);
    g.destroy();
  }
  if (!scene.textures.exists(TILE_SIDE_E_KEY)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    drawSideFaceE(g, 0x265a30, 0x143a1f);
    g.generateTexture(TILE_SIDE_E_KEY, TILE.halfW, TILE.halfH + ELEV_PX * ELEV_MAX);
    g.destroy();
  }
  if (!scene.textures.exists(PROP_TEXTURE_KEY)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x6b4a2a, 1);
    g.fillRect(13, 24, 6, 14);
    g.fillStyle(0x2a5a3a, 1);
    g.fillCircle(16, 18, 12);
    g.lineStyle(1, 0x143a1f, 1);
    g.strokeCircle(16, 18, 12);
    g.generateTexture(PROP_TEXTURE_KEY, 32, 40);
    g.destroy();
  }
  if (!scene.textures.exists(WATER_TEXTURE_KEY)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    drawIsoDiamond(g, 0x1a5a8a, 0x144a6a);
    g.generateTexture(WATER_TEXTURE_KEY, TILE.width, TILE.height);
    g.destroy();
  }
}

function drawIsoDiamond(g: Phaser.GameObjects.Graphics, fill: number, stroke: number): void {
  g.lineStyle(1, stroke, 1);
  g.fillStyle(fill, 1);
  g.beginPath();
  g.moveTo(TILE.halfW, 0);
  g.lineTo(TILE.width, TILE.halfH);
  g.lineTo(TILE.halfW, TILE.height);
  g.lineTo(0, TILE.halfH);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

/** South-facing (bottom-left) side face parallelogram. */
function drawSideFaceS(g: Phaser.GameObjects.Graphics, fill: number, stroke: number): void {
  const h = ELEV_PX * ELEV_MAX;
  g.lineStyle(1, stroke, 0.6);
  g.fillStyle(fill, 1);
  g.beginPath();
  g.moveTo(0, 0);
  g.lineTo(TILE.halfW, TILE.halfH);
  g.lineTo(TILE.halfW, TILE.halfH + h);
  g.lineTo(0, h);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

/** East-facing (bottom-right) side face parallelogram. */
function drawSideFaceE(g: Phaser.GameObjects.Graphics, fill: number, stroke: number): void {
  const h = ELEV_PX * ELEV_MAX;
  g.lineStyle(1, stroke, 0.6);
  g.fillStyle(fill, 1);
  g.beginPath();
  g.moveTo(0, TILE.halfH);
  g.lineTo(TILE.halfW, 0);
  g.lineTo(TILE.halfW, h);
  g.lineTo(0, TILE.halfH + h);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

/**
 * Builds an isometric island from the server-authoritative `ZoneGrid`.
 * `zone_grid.kinds` (per-tile :grass/:water), `zone_grid.height`
 * (per-tile elevation units), `zone_grid.trees`, `zone_grid.solid_props`,
 * and `zone_grid.blocked` drive every visual + collision decision —
 * nothing is generated locally.
 */
export function buildProceduralIsoMap(
  scene: Phaser.Scene,
  origin: ScreenPos,
  zoneGrid: ZoneGrid,
): IsoMap {
  ensureMapTextures(scene);
  ensureWateringCanTexture(scene);
  ensurePropTextures(scene);

  const size = zoneGrid.size || MAP_SIZE;
  const heightMap = gridToNumberRows(zoneGrid.height, size);
  const waterMap = kindsToWaterMap(zoneGrid.kinds, size);
  const container = scene.add.container(origin.sx, origin.sy);
  container.setDepth(-100000);

  const waterTiles: Phaser.GameObjects.Image[] = [];

  for (let ty = 0; ty < size; ty++) {
    for (let tx = 0; tx < size; tx++) {
      const { sx, sy } = worldToScreen(tx, ty);

      if (waterMap[ty]![tx]) {
        const tile = scene.add.image(sx, sy, WATER_TEXTURE_KEY);
        tile.setOrigin(0.5, 0);
        tile.setAlpha(0.85 + ((tx + ty) % 3) * 0.05);
        container.add(tile);
        waterTiles.push(tile);
      } else {
        const elev = heightMap[ty]![tx]!;
        const elevPx = elev * ELEV_PX;

        const tile = scene.add.image(sx, sy - elevPx, TILE_TEXTURE_KEY);
        tile.setOrigin(0.5, 0);
        tile.setAlpha((tx + ty) % 2 === 0 ? 1 : 0.92);
        container.add(tile);

        if (elev > 0) {
          const southIsWater = ty < size - 1 && waterMap[ty + 1]![tx];
          const southElev = southIsWater ? 0 : (ty < size - 1 ? heightMap[ty + 1]![tx]! : 0);
          const diff = elev - southElev;
          if (diff > 0) {
            const side = scene.add.image(sx - TILE.halfW, sy + TILE.halfH - elevPx, TILE_SIDE_S_KEY);
            side.setOrigin(0, 0);
            side.setCrop(0, 0, TILE.halfW, TILE.halfH + diff * ELEV_PX);
            container.add(side);
          }
        }

        if (elev > 0) {
          const eastIsWater = tx < size - 1 && waterMap[ty]![tx + 1];
          const eastElev = eastIsWater ? 0 : (tx < size - 1 ? heightMap[ty]![tx + 1]! : 0);
          const diff = elev - eastElev;
          if (diff > 0) {
            const side = scene.add.image(sx, sy + TILE.halfH - elevPx, TILE_SIDE_E_KEY);
            side.setOrigin(0, 0);
            side.setCrop(0, 0, TILE.halfW, TILE.halfH + diff * ELEV_PX);
            container.add(side);
          }
        }
      }
    }
  }

  // Animate water: gentle alpha oscillation staggered per tile
  for (let i = 0; i < waterTiles.length; i++) {
    const tile = waterTiles[i]!;
    scene.tweens.add({
      targets: tile,
      alpha: { from: tile.alpha, to: tile.alpha - 0.15 },
      duration: 1800 + (i % 5) * 200,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
      delay: (i % 7) * 150,
    });
  }

  const trees: InteractableTree[] = [];
  for (const [tx, ty] of zoneGrid.trees) {
    if (waterMap[ty]?.[tx]) continue;
    const elev = heightMap[ty]?.[tx] ?? 0;
    trees.push(new InteractableTree(scene, origin, tx, ty, elev));
  }

  const decorations = placeZoneDecorations(scene, origin, heightMap, waterMap);
  const collision = CollisionMap.fromGrid(zoneGrid.blocked);

  return { container, origin, heightMap, waterMap, collision, trees, decorations };
}

function gridToNumberRows(grid: number[][] | undefined, size: number): number[][] {
  if (grid && grid.length === size) return grid;
  return Array.from({ length: size }, () => new Array<number>(size).fill(0));
}

function kindsToWaterMap(kinds: string[][] | undefined, size: number): boolean[][] {
  if (!kinds || kinds.length !== size) {
    return Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  }
  return kinds.map((row) => row.map((k) => k === "water"));
}
